import React from 'react';
import { Facebook, Twitter, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { partyInfo } from '../mockData';
import AppLink from './AppLink';

const Footer = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { name: { en: 'About Us', bn: 'আমাদের সম্পর্কে' }, href: '/about' },
    { name: { en: 'Leadership', bn: 'নেতৃত্ব' }, href: '/leadership' },
    { name: { en: 'Our Manifesto', bn: 'আমাদের ইশতেহার' }, href: '/manifesto' },
    { name: { en: 'News', bn: 'সংবাদ' }, href: '/news' },
  ];

  const getInvolved = [
    { name: { en: 'Become a Member', bn: 'সদস্য হন' }, href: '/join' },
    { name: { en: 'Member Directory', bn: 'সদস্য তালিকা' }, href: '/directory' },
    { name: { en: 'Donate', bn: 'দান করুন' }, href: '/donate' },
    { name: { en: 'Events', bn: 'ইভেন্ট' }, href: '/events' },
  ];

  return (
    <footer className="bg-gray-900 pb-14" data-testid="main-footer">
      {/* Flag color bar */}
      <div className="flex h-1.5">
        <div className="flex-1 bg-party-red"></div>
        <div className="flex-1 bg-white"></div>
        <div className="flex-1 bg-party-green"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Party Info */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <img
                src={partyInfo.flagImage}
                alt="NDP Flag"
                className="h-14 w-14 rounded-lg object-contain"
              />
              <div>
                <div className="text-xl font-bold text-white">NDP</div>
                <div className="text-gray-400 text-sm">{t(partyInfo.shortName)}</div>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t({
                en: 'Building a democratic, inclusive, and prosperous Bangladesh.',
                bn: 'একটি গণতান্ত্রিক, অন্তর্ভুক্তিমূলক এবং সমৃদ্ধ বাংলাদেশ গড়ি।',
              })}
            </p>
            {/* Social Media */}
            <div className="flex space-x-4 mt-6">
              <a href="https://www.facebook.com/profile.php?id=100066695862611" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#1877F2] transition-colors" data-testid="social-facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <span className="text-gray-400 hover:text-party-red transition-colors cursor-pointer" data-testid="social-twitter">
                <Twitter className="h-5 w-5" />
              </span>
              <span className="text-gray-400 hover:text-party-red transition-colors cursor-pointer" data-testid="social-youtube">
                <Youtube className="h-5 w-5" />
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">
              {t({ en: 'Quick Links', bn: 'দ্রুত লিঙ্ক' })}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <AppLink to={link.href} className="text-gray-400 hover:text-party-green transition-colors text-sm">
                    {t(link.name)}
                  </AppLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Get Involved */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">
              {t({ en: 'Get Involved', bn: 'জড়িত হন' })}
            </h3>
            <ul className="space-y-3">
              {getInvolved.map((link) => (
                <li key={link.href}>
                  <AppLink to={link.href} className="text-gray-400 hover:text-party-green transition-colors text-sm">
                    {t(link.name)}
                  </AppLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Shop */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">{t({ en: 'Shop', bn: 'শপ' })}</h3>
            <ul className="space-y-3">
              {[['/shop', { en: 'Official Store', bn: 'অফিসিয়াল স্টোর' }], ['/order', { en: 'Track order', bn: 'অর্ডার ট্র্যাক' }], ['/shop/size-guide', { en: 'Size guide', bn: 'সাইজ গাইড' }], ['/shop/shipping-returns', { en: 'Shipping & returns', bn: 'শিপিং ও রিটার্ন' }], ['/shop/faq', { en: 'FAQ', bn: 'প্রশ্নোত্তর' }], ['/shop/media-kit', { en: 'Media kit', bn: 'মিডিয়া কিট' }], ['/shop/terms', { en: 'Terms of sale', bn: 'বিক্রয়ের শর্ত' }], ['/shop/privacy', { en: 'Privacy', bn: 'গোপনীয়তা' }]].map(([href, name]) => (
                <li key={href}><AppLink to={href} className="text-gray-400 hover:text-party-green transition-colors text-sm" data-testid={`footer-shop-${href.split('/').pop()}`}>{t(name)}</AppLink></li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-gray-500">{t({ en: "Proceeds are recorded in the party's audited accounts.", bn: 'আয় দলের নিরীক্ষিত হিসাবে লিপিবদ্ধ হয়।' })}</p>
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="footer-payment-logos">{['bKash', 'Nagad', 'VISA', 'Mastercard', 'COD'].map((m) => <span key={m} className="rounded bg-white px-1.5 py-0.5 text-[10px] font-bold text-gray-700">{m}</span>)}</div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">
              {t({ en: 'Contact Us', bn: 'যোগাযোগ' })}
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-party-red flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">{t(partyInfo.address)}</span>
              </li>
              {partyInfo.phones.map((phone) => (
                <li key={phone} className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-party-green flex-shrink-0" />
                  <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-gray-400 text-sm hover:text-party-green transition-colors">{phone}</a>
                </li>
              ))}
              <li className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-party-red flex-shrink-0" />
                <a href={`mailto:${partyInfo.email}`} className="text-gray-400 text-sm hover:text-party-red transition-colors underline">{partyInfo.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} {t(partyInfo.name)}. {t({ en: 'All rights reserved.', bn: 'সর্বস্বত্ব সংরক্ষিত।' })}
          </p>
          <AppLink to="/admin/login" className="text-gray-500 hover:text-party-red text-sm transition-colors" data-testid="footer-admin-link">
            {t({ en: 'Admin Panel', bn: 'অ্যাডমিন প্যানেল' })}
          </AppLink>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
